import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence, Variants, Transition } from 'framer-motion';
import { Box, Paper, Typography, Button as MuiButton, IconButton } from '@mui/material';
import { ArrowLeft, Volume2, Eraser } from 'lucide-react';
import { useThemeContext } from './ThemeWrapper';

import Sidebar from './Sidebar';
import RightPanel from './RightPanel';

// Define the type for a character item in the tracing practice
type TracingCharacter = {
    char: string;
    romaji: string;
    audio: string; // Romaji for audio file name
    meaning?: string; // Optional for Kanji
};

// Define a type for the drawing context reference
type CanvasDrawingContext = {
    ctx: CanvasRenderingContext2D | null;
    isDrawing: boolean;
};

/**
 * Dynamic Character Tracing Page Component.
 * Displays characters for tracing based on the scriptType parameter.
 */
const CharacterTracingPage: React.FC = () => {
    const navigate = useNavigate();
    const { scriptType } = useParams<{ scriptType: 'hiragana' | 'katakana' | 'kanji' }>();
    const { isDarkTheme } = useThemeContext();

    // Data for Hiragana Tracing Characters
    const hiraganaTracingCharacters: TracingCharacter[] = [
        { char: 'あ', romaji: 'a', audio: 'a' }, { char: 'い', romaji: 'i', audio: 'i' },
        { char: 'う', romaji: 'u', audio: 'u' }, { char: 'え', romaji: 'e', audio: 'e' },
        { char: 'お', romaji: 'o', audio: 'o' }, { char: 'か', romaji: 'ka', audio: 'ka' },
        { char: 'き', romaji: 'ki', audio: 'ki' }, { char: 'く', romaji: 'ku', audio: 'ku' },
        { char: 'け', romaji: 'ke', audio: 'ke' }, { char: 'こ', romaji: 'ko', audio: 'ko' },
        { char: 'さ', romaji: 'sa', audio: 'sa' }, { char: 'し', romaji: 'shi', audio: 'shi' },
        { char: 'す', romaji: 'su', audio: 'su' }, { char: 'せ', romaji: 'se', audio: 'se' },
        { char: 'そ', romaji: 'so', audio: 'so' }, { char: 'た', romaji: 'ta', audio: 'ta' },
        { char: 'ち', romaji: 'chi', audio: 'chi' }, { char: 'つ', romaji: 'tsu', audio: 'tsu' },
        { char: 'て', romaji: 'te', audio: 'te' }, { char: 'と', romaji: 'to', audio: 'to' },
    ];

    // Data for Katakana Tracing Characters
    const katakanaTracingCharacters: TracingCharacter[] = [
        { char: 'ア', romaji: 'a', audio: 'a' }, { char: 'イ', romaji: 'i', audio: 'i' },
        { char: 'ウ', romaji: 'u', audio: 'u' }, { char: 'エ', romaji: 'e', audio: 'e' },
        { char: 'オ', romaji: 'o', audio: 'o' }, { char: 'カ', romaji: 'ka', audio: 'ka' },
        { char: 'キ', romaji: 'ki', audio: 'ki' }, { char: 'ク', romaji: 'ku', audio: 'ku' },
        { char: 'ケ', romaji: 'ke', audio: 'ke' }, { char: 'コ', romaji: 'ko', audio: 'ko' },
        { char: 'サ', romaji: 'sa', audio: 'sa' }, { char: 'シ', romaji: 'shi', audio: 'shi' },
        { char: 'ス', romaji: 'su', audio: 'su' }, { char: 'セ', romaji: 'se', audio: 'se' },
        { char: 'ソ', romaji: 'so', audio: 'so' }, { char: 'タ', romaji: 'ta', audio: 'ta' },
        { char: 'チ', romaji: 'chi', audio: 'chi' }, { char: 'ツ', romaji: 'tsu', audio: 'tsu' },
        { char: 'テ', romaji: 'te', audio: 'te' }, { char: 'ト', romaji: 'to', audio: 'to' },
    ];

    // Data for Kanji Tracing Characters (simplified for example)
    const kanjiTracingCharacters: TracingCharacter[] = [
        { char: '日', romaji: 'nichi', audio: 'nichi', meaning: 'sun/day' },
        { char: '一', romaji: 'ichi', audio: 'ichi', meaning: 'one' },
        { char: '人', romaji: 'hito', audio: 'hito', meaning: 'person' },
        { char: '大', romaji: 'dai', audio: 'dai', meaning: 'big' },
        { char: '本', romaji: 'hon', audio: 'hon', meaning: 'book/origin' },
        { char: '中', romaji: 'naka', audio: 'naka', meaning: 'middle/inside' },
        { char: '出', romaji: 'deru', audio: 'deru', meaning: 'exit/go out' },
        { char: '学', romaji: 'gaku', audio: 'gaku', meaning: 'learn' },
        { char: '高', romaji: 'takai', audio: 'takai', meaning: 'high/expensive' },
        { char: '円', romaji: 'en', audio: 'en', meaning: 'yen/circle' },
    ];

    let currentTracingCharacters: TracingCharacter[] = [];
    let pageTitle = "";
    let pageSubtitle = "";

    if (scriptType === 'hiragana') {
        currentTracingCharacters = hiraganaTracingCharacters;
        pageTitle = "Hiragana Tracing Practice";
        pageSubtitle = "Practice tracing Hiragana characters to master their stroke order.";
    } else if (scriptType === 'katakana') {
        currentTracingCharacters = katakanaTracingCharacters;
        pageTitle = "Katakana Tracing Practice";
        pageSubtitle = "Practice tracing Katakana characters to master their stroke order.";
    } else if (scriptType === 'kanji') {
        currentTracingCharacters = kanjiTracingCharacters;
        pageTitle = "Kanji Tracing Practice";
        pageSubtitle = "Practice tracing Kanji characters to master their stroke order and meaning.";
    } else {
        currentTracingCharacters = hiraganaTracingCharacters;
        pageTitle = "Tracing Practice";
        pageSubtitle = "Select a script to begin tracing practice.";
    }

    // Refs for each canvas element
    const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
    // Ref to store drawing contexts and their drawing status, NOT triggering re-renders
    const drawingContexts = useRef<CanvasDrawingContext[]>([]);

    // Determine drawing color based on theme for contrast
    const drawingColor = isDarkTheme ? '#E0E0E0' : '#333333';
    const placeholderColor = isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';

    // Function to draw the placeholder character
    const drawPlaceholder = (ctx: CanvasRenderingContext2D, char: string, canvasWidth: number, canvasHeight: number) => {
        ctx.save();
        ctx.clearRect(0, 0, canvasWidth, canvasHeight); // Clear current drawing
        ctx.fillStyle = isDarkTheme ? '#2d3748' : '#e0f2f7';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        ctx.font = `${Math.min(canvasWidth, canvasHeight) * 0.8}px 'Noto Sans JP', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = placeholderColor;
        ctx.fillText(char, canvasWidth / 2, canvasHeight / 2);
        ctx.restore();
    };

    // Effect to set canvas dimensions dynamically and draw placeholders on mount or script/theme change
    useEffect(() => {
        // Initialize drawingContexts ref for the current set of characters
        drawingContexts.current = currentTracingCharacters.map(() => ({ ctx: null, isDrawing: false }));

        canvasRefs.current.forEach((canvas, index) => {
            if (canvas && currentTracingCharacters[index]) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    drawingContexts.current[index] = { ctx, isDrawing: false }; // Store context
                    canvas.width = canvas.offsetWidth;
                    canvas.height = canvas.offsetHeight;
                    drawPlaceholder(ctx, currentTracingCharacters[index].char, canvas.width, canvas.height);
                }
            }
        });

        const preventScroll = (e: TouchEvent) => {
            // Prevent scroll only if *any* canvas is currently being drawn on
            if (drawingContexts.current.some(item => item?.isDrawing)) {
                e.preventDefault();
            }
        };
        document.addEventListener('touchmove', preventScroll, { passive: false });

        const handleResize = () => {
            canvasRefs.current.forEach((canvas, index) => {
                if (canvas && currentTracingCharacters[index]) {
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        // To persist drawing on resize, you'd need to save/restore image data.
                        // For now, we'll just redraw the placeholder, clearing previous strokes on resize.
                        canvas.width = canvas.offsetWidth;
                        canvas.height = canvas.offsetHeight;
                        drawPlaceholder(ctx, currentTracingCharacters[index].char, canvas.width, canvas.height);
                    }
                }
            });
        };
        window.addEventListener('resize', handleResize);

        return () => {
            document.removeEventListener('touchmove', preventScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [isDarkTheme, scriptType, currentTracingCharacters]); // Re-run if theme or scriptType changes

    // Helper to get drawing coordinates from mouse or touch events
    const getCoordinates = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
        const rect = canvas.getBoundingClientRect();
        if (e.nativeEvent instanceof TouchEvent) {
            const touch = e.nativeEvent.touches[0];
            return {
                x: touch.clientX - rect.left,
                y: touch.clientY - rect.top
            };
        } else {
            return {
                x: e.nativeEvent.offsetX,
                y: e.nativeEvent.offsetY
            };
        }
    };

    // Start drawing path
    const startDrawing = (e: React.MouseEvent | React.TouchEvent, index: number) => {
        const ctxItem = drawingContexts.current[index];
        const canvas = canvasRefs.current[index];
        if (!ctxItem || !ctxItem.ctx || !canvas) return;

        const { x, y } = getCoordinates(e, canvas);
        ctxItem.ctx.beginPath();
        ctxItem.ctx.moveTo(x, y);
        ctxItem.isDrawing = true; // Set drawing state for this specific canvas
    };

    // Continue drawing path
    const draw = (e: React.MouseEvent | React.TouchEvent, index: number) => {
        const ctxItem = drawingContexts.current[index];
        const canvas = canvasRefs.current[index];
        if (!ctxItem || !ctxItem.isDrawing || !ctxItem.ctx || !canvas) return;

        const { x, y } = getCoordinates(e, canvas);
        ctxItem.ctx.lineTo(x, y);
        ctxItem.ctx.strokeStyle = drawingColor;
        ctxItem.ctx.lineWidth = 4;
        ctxItem.ctx.lineCap = 'round';
        ctxItem.ctx.stroke();
    };

    // Stop drawing path (only stops the active drawing stroke, doesn't clear canvas)
    const stopDrawing = (index: number) => {
        const ctxItem = drawingContexts.current[index];
        if (ctxItem) {
            ctxItem.isDrawing = false; // Stop drawing for this specific canvas
        }
    };

    // Clear the canvas for a specific character and redraw placeholder
    const handleClear = (index: number) => {
        const canvas = canvasRefs.current[index];
        if (!canvas || !currentTracingCharacters[index]) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        drawPlaceholder(ctx, currentTracingCharacters[index].char, canvas.width, canvas.height);
    };

    /**
     * Plays an audio file for a given romaji character.
     * Uses process.env.PUBLIC_URL for robust path resolution.
     */
    const playAudio = (audioFileName: string) => {
        if (!audioFileName) return;
        const audio = new Audio(`${process.env.PUBLIC_URL}/audio/${audioFileName}.mp3`);
        audio.play().catch(err => {
            console.error(`Audio file not found at: ${process.env.PUBLIC_URL}/audio/${audioFileName}.mp3`, err);
        });
    };

    // Framer Motion variants for staggered grid animation
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05,
            },
        },
    };

    // Correctly typed spring transition for Framer Motion
    const springTransition: Transition = {
        type: "spring",
        stiffness: 100,
        damping: 10
    };

    // Framer Motion variants for individual character boxes
    const itemVariants: Variants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: springTransition,
        },
    };

    return (
        <div className={`flex ${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'} min-h-screen font-inter`}>
            <Sidebar />

            <main className={`flex-1 p-8 pb-20 md:p-12 overflow-y-auto ${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'}`}>
                <motion.div
                    className="md:hidden absolute top-4 left-4 sm:top-8 sm:left-8 z-10 mt-8"
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 100, damping: 10 }}
                >
                    <MuiButton
                        variant="contained"
                        color="primary"
                        startIcon={<ArrowLeft size={20} />}
                        onClick={() => navigate("/hiragana")}
                         className={`backdrop-blur-md !font-bold !py-2 !px-4 !rounded-xl !shadow-md ${
                          isDarkTheme
                         ? '!bg-gray-800/40 hover:!bg-gray-800/60 !text-white'
                          : '!bg-gray-300/40 hover:!bg-gray-300/60 !text-black'
                           }`}
                        component={motion.button}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Back
                    </MuiButton>
                </motion.div>

                <Typography variant="h3" component="h1" className={`!font-extrabold mb-2 ${isDarkTheme ? 'text-white' : 'text-black'}`}>
                    {pageTitle}
                </Typography>
                <Typography variant="body1" component="p" className={`text-lg mb-6 ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}>
                    {pageSubtitle}
                </Typography>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={scriptType}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={containerVariants}
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                    >
                        {currentTracingCharacters.map((charData, index) => (
                            <motion.div
                                key={charData.char}
                                variants={itemVariants}
                                className={`p-4 rounded-xl shadow-lg border-2 cursor-pointer transition-transform duration-200 flex flex-col items-center ${isDarkTheme ? 'bg-zinc-900 border-gray-700 hover:border-emerald-400' : 'bg-white border-gray-200 hover:border-emerald-600'}`}
                            >
                                <Box className="flex items-center mb-4">
                                    <IconButton onClick={() => playAudio(charData.audio)} size="large" className={`${isDarkTheme ? '!text-pink-300' : '!text-pink-500'}`}>
                                        <Volume2 size={32} />
                                    </IconButton>
                                    <Typography variant="h4" className={`!font-bold ml-2 ${isDarkTheme ? 'text-white' : 'text-black'}`}>
                                        {charData.char}
                                    </Typography>
                                    <Typography variant="h6" className={`ml-2 ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {charData.romaji}
                                    </Typography>
                                    {charData.meaning && (
                                        <Typography variant="body2" className={`ml-2 ${isDarkTheme ? 'text-gray-400' : 'text-gray-600'}`}>
                                            ({charData.meaning})
                                        </Typography>
                                    )}
                                </Box>

                                <div className={`relative w-full aspect-square max-w-[250px] rounded-lg overflow-hidden shadow-inner border-2 ${isDarkTheme ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-gray-200'}`}>
                                    <canvas
                                        ref={el => { if (el) canvasRefs.current[index] = el; }}
                                        className="w-full h-full block"
                                        onMouseDown={(e) => startDrawing(e, index)}
                                        onMouseMove={(e) => draw(e, index)}
                                        onMouseUp={() => stopDrawing(index)}
                                        onTouchStart={(e) => startDrawing(e, index)}
                                        onTouchMove={(e) => draw(e, index)}
                                        onTouchEnd={() => stopDrawing(index)}
                                    />
                                </div>

                                <MuiButton
                                    variant="outlined"
                                    startIcon={<Eraser size={20} />}
                                    onClick={() => handleClear(index)}
                                    className={`!mt-4 !font-bold !py-2 !px-4 !rounded-xl !shadow-md ${isDarkTheme ? '!bg-gray-700 !text-pink-300 hover:!bg-gray-600' : '!bg-pink-100 !text-pink-600 hover:!bg-pink-200'}`}
                                    component={motion.button}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    Clear
                                </MuiButton>
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatePresence>
            </main>

            <RightPanel />
        </div>
    );
};

export default CharacterTracingPage;
